import {
  CircularProgress,
  TextField,
  useTheme
} from "@mui/material";
import { Paperclip, SendHorizontal } from "lucide-react";
import React, { ChangeEvent, FormEvent, RefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, InputPill } from "./AISupportChatMessageInput.styles";
import { ButtonDigited } from "../../../../components/Button/Button.variants";
import { ImageContextFile } from "../../AISupportChat.types";
import { ALLOWED_IMAGE_TYPES } from "../../const";

interface AISupportChatMessageInputProps {
  onSendMessage: (message: string) => void;
  onUploadImage?: (file: File) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isHandlingImage?: boolean;
  isPrelogin?: boolean;
  inputRef: RefObject<HTMLInputElement>;
   listImgContext: ImageContextFile[]
}

const max_screen_uploaded = 3;

const AISupportChatMessageInput = ({
  onSendMessage,
  onUploadImage,
  disabled,
  isLoading,
  isHandlingImage,
  inputRef,
  isPrelogin,
  listImgContext
}: AISupportChatMessageInputProps) => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUploadImage?.(file);
    e.target.value = "";
  };

  const sendMessage = (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    if (!message) return;
    setMessage("");
    onSendMessage(message);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.altKey && !e.shiftKey) {
      sendMessage();
      e.preventDefault();
    }
  };

  return (
    <Container gap={ "10px" }>
      { !isPrelogin && (
        <>
          <input
            ref={ fileInputRef }
            type="file"
            accept={ ALLOWED_IMAGE_TYPES.join(",") }
            style={ { display: "none" } }
            onChange={ handleFileChange }
          />
          <ButtonDigited
            id={ "agent-cta-upload-screen" }
            data-testid="agent-cta-upload-screen"
            aria-label={ t("upload-screen") }
            title={  t("upload-screen") }
            disabled={ disabled || isHandlingImage || listImgContext.length >= max_screen_uploaded }
            typology="icononly"
            onClick={ () => fileInputRef.current?.click() }
          >
            { isHandlingImage
              ? <CircularProgress size={ 14 } />
              : <Paperclip size={ "1rem" }/> }
          </ButtonDigited>
        </>
      ) }
      <InputPill theme={ theme }>
        <form onSubmit={ sendMessage } style={ { width: "100%" } }>
          <TextField
            disabled={ disabled }
            inputRef={ inputRef }
            variant={ "standard" }
            placeholder={ t("ai_support_chat_input_ph") || "" }
            size={ "small" }
            value={ message }
            sx={ { padding: 0, width: "100%" } }
            multiline={ true }
            maxRows={ 2 }
            onKeyDown={ (event) => handleInputKeyDown(event) }
            onChange={ (event) => setMessage(event.target.value) }
            InputProps={ {
              disableUnderline: true,
              sx: {
                color: theme.customColors.textInput,
                padding: 0
              }
            } }
          />
        </form>
      </InputPill>
      <ButtonDigited
        typology="primary"
        onClick={ () => sendMessage() }
        disabled={ !message || disabled || isLoading }
        endIcon={ isLoading
          ? <CircularProgress size={ 16 } />
          : <SendHorizontal size={ 16 } /> }
      >
        { t("ai_support_chat_send") }
      </ButtonDigited>
      { /* <Button
        onClick={ () => sendMessage() }
        disabled={ !message || disabled || isLoading }
        endIcon={ isLoading
          ? <CircularProgress size={ 16 } />
          : <SendHorizontal size={ 16 } /> }
      >
        { t("ai_support_chat_send") }
      </Button> */ }
    </Container>
  );
};

export default AISupportChatMessageInput;
